/**
 * 자녀 등록/관리 시트 모달
 *  - 학부모 가입 직후 (홈 진입 시 자동 오픈, fromSignup=true)
 *  - 마이페이지 "자녀 관리" 메뉴 클릭 시 (fromSignup=false)
 *
 * 자녀 데이터 모델: users.children = [{ id, name, birthYear, photoURL }]
 *  - photoURL 없으면 기본 캐릭터 SVG (/avatar/child-default.svg)
 *  - Storage 경로: children/{uid}/{childId}.webp
 */
import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { IoCameraOutline, IoClose, IoTrashOutline } from 'react-icons/io5';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { db, storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useDialog } from '../../context/DialogContext';
import { compressImage } from '../../utils/imageUtils';
import ChildAvatar from './ChildAvatar';

const buildYearOptions = () => {
  const now = new Date().getFullYear();
  const arr = [];
  for (let y = now; y >= now - 18; y -= 1) arr.push(y);
  return arr;
};

const koreanAge = (year, now) => (year ? now - year + 1 : null);

const ChildrenSheet = ({ open, onClose }) => {
  const { userData, refreshUser } = useAuth();
  const { confirm, alert: dialogAlert } = useDialog();

  const yearOptions = useMemo(buildYearOptions, []);
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const children = userData?.children || [];

  const [form, setForm] = useState({ name: '', birthYear: '', file: null, previewUrl: '' });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  if (!open) return null;

  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (form.previewUrl) URL.revokeObjectURL(form.previewUrl);
    setForm((f) => ({ ...f, file, previewUrl: URL.createObjectURL(file) }));
    e.target.value = '';
  };

  const hasFormInput = Boolean(form.name.trim() && form.birthYear);

  const addChild = async () => {
    if (!hasFormInput || saving || !userData?.uid) return;
    setSaving(true);
    try {
      const childId = `child-${Date.now()}`;
      let photoURL = '';
      if (form.file) {
        const compressed = await compressImage(form.file, 320, 0.85);
        const storageRef = ref(storage, `children/${userData.uid}/${childId}.webp`);
        await uploadBytes(storageRef, compressed);
        photoURL = await getDownloadURL(storageRef);
      }
      const next = [
        ...(userData.children || []),
        { id: childId, name: form.name.trim(), birthYear: Number(form.birthYear), photoURL },
      ];
      await updateDoc(doc(db, 'users', userData.uid), { children: next });
      await refreshUser();
      if (form.previewUrl) URL.revokeObjectURL(form.previewUrl);
      setForm({ name: '', birthYear: '', file: null, previewUrl: '' });
    } catch (e) {
      console.error('자녀 추가 실패:', e);
      dialogAlert('자녀 추가에 실패했어요. 다시 시도해 주세요.');
    } finally {
      setSaving(false);
    }
  };

  const handleDone = async () => {
    if (hasFormInput) {
      await addChild();
    }
    onClose();
  };

  const removeChild = async (child) => {
    const ok = await confirm({
      title: '자녀 삭제',
      message: `${child.name} 자녀 정보를 삭제할까요?`,
      confirmText: '삭제',
      danger: true,
    });
    if (!ok) return;
    const next = (userData.children || []).filter((c) => c.id !== child.id);
    await updateDoc(doc(db, 'users', userData.uid), { children: next });
    if (child.photoURL) {
      deleteObject(ref(storage, `children/${userData.uid}/${child.id}.webp`)).catch(() => {});
    }
    await refreshUser();
  };

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Head>
          <Title>자녀 관리</Title>
          <CloseBtn type="button" onClick={onClose} aria-label="닫기">
            <IoClose size={22} />
          </CloseBtn>
        </Head>
        <Body>
          {children.length > 0 && (
            <List>
              {children.map((c) => (
                <ChildCard key={c.id}>
                  <ChildAvatar photoURL={c.photoURL} name={c.name} size={44} />
                  <ChildInfo>
                    <ChildName>{c.name}</ChildName>
                    {c.birthYear ? (
                      <ChildMeta>{c.birthYear}년생 · {koreanAge(c.birthYear, currentYear)}살</ChildMeta>
                    ) : (
                      <ChildMeta $hint>출생연도 미입력</ChildMeta>
                    )}
                  </ChildInfo>
                  <DelBtn type="button" onClick={() => removeChild(c)} aria-label="자녀 삭제">
                    <IoTrashOutline size={18} />
                  </DelBtn>
                </ChildCard>
              ))}
            </List>
          )}

          <Form>
            <AvatarRow>
              <AvatarPicker type="button" onClick={() => fileRef.current?.click()}>
                <ChildAvatar photoURL={form.previewUrl} name="자녀 사진" size={84} />
                <CamBadge><IoCameraOutline size={14} /></CamBadge>
              </AvatarPicker>
              <AvatarHint>
                {form.file ? '사진을 바꾸려면 다시 눌러주세요' : '사진은 선택이에요. 안 올리면 기본 캐릭터로 표시돼요.'}
              </AvatarHint>
              <HiddenInput ref={fileRef} type="file" accept="image/*" onChange={pickFile} />
            </AvatarRow>

            <Field>
              <FieldLabel>이름</FieldLabel>
              <Input
                value={form.name}
                maxLength={16}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>

            <Field>
              <FieldLabel>출생연도</FieldLabel>
              <Select
                value={form.birthYear}
                onChange={(e) => setForm({ ...form, birthYear: e.target.value })}
              >
                <option value="">선택</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}년생 ({koreanAge(y, currentYear)}살)
                  </option>
                ))}
              </Select>
            </Field>
          </Form>
        </Body>

        <Actions>
          <DoneBtn type="button" disabled={saving} onClick={handleDone}>
            {saving ? '저장 중...' : '완료'}
          </DoneBtn>
        </Actions>
      </Sheet>
    </Overlay>
  );
};

export default ChildrenSheet;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 440px;
  max-height: 88vh;
  background: #fff;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 6px;
`;

const Title = styled.p`
  font-size: 19px;
  font-weight: 800;
  color: #1F2937;
  letter-spacing: -0.4px;
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  color: #6B7280;
  border-radius: 8px;
  &:hover { background: #F3F4F6; }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 20px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
`;

const ChildCard = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px 12px;
  border: 1.5px solid #E0E7FF;
  background: #F5F7FF;
  border-radius: 12px;
`;

const ChildInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChildName = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: #1F2937;
`;

const ChildMeta = styled.p`
  margin-top: 2px;
  font-size: 12.5px;
  font-weight: 500;
  color: ${({ $hint }) => ($hint ? '#9CA3AF' : '#6B7280')};
`;

const DelBtn = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  color: #9CA3AF;
  border-radius: 8px;
  &:hover { color: #EF4444; background: #FEF2F2; }
`;

const Form = styled.div`
  margin-bottom: 16px;
  padding-top: 4px;
`;

const AvatarRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
`;

const AvatarPicker = styled.button`
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: #F3F4F6;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(74, 124, 254, 0.15);
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
`;

const CamBadge = styled.span`
  position: absolute;
  right: -2px;
  bottom: -2px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #4A7CFE;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
`;

const AvatarHint = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  text-align: center;
`;

const HiddenInput = styled.input`
  display: none;
`;

const Field = styled.div`
  margin-bottom: 10px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12.5px;
  font-weight: 700;
  color: #4B5563;
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border: 1.5px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14.5px;
  font-weight: 500;
  color: #1F2937;
  background: #fff;
  &:focus { outline: none; border-color: #4A7CFE; }
`;

const Select = styled.select`
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border: 1.5px solid #E5E7EB;
  border-radius: 10px;
  font-size: 14.5px;
  font-weight: 500;
  color: #1F2937;
  background: #fff;
  &:focus { outline: none; border-color: #4A7CFE; }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px 20px calc(14px + env(safe-area-inset-bottom));
  border-top: 1px solid #F3F4F6;
  background: #fff;
`;

const DoneBtn = styled.button`
  flex: 1;
  height: 48px;
  border-radius: 12px;
  background: #4A7CFE;
  color: #fff;
  font-size: 14.5px;
  font-weight: 700;
  &:disabled { background: #BFD0FF; cursor: not-allowed; }
`;
